<template>
  <div
    class="card h-100 border-0 open-game-card overflow-hidden"
    :class="isDarkMode ? 'open-game-card-dark' : 'open-game-card-light'"
    role="link"
    tabindex="0"
    @click="onCardClick"
    @keydown.enter="onCardClick"
  >
    <div class="card-body p-4 pb-4 d-flex flex-column">
      <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
        <span
          class="badge px-3 fw-bold register-badge"
          :class="isDarkMode ? 'badge-prize-dark' : 'badge-prize-light'"
          role="button"
          @click.stop.prevent="addToGameHandler"
        >
          <span class="badge-line register-prefix">{{ registerBadge.prefix }}</span>
          <span v-if="registerBadge.term" class="badge-line register-term">{{ registerBadge.term }}</span>
        </span>
        <span
          v-if="prizeCount || prizeVolume"
          class="badge px-3 fw-bold prize-badge"
          :class="[isDarkMode ? 'badge-prize-dark' : 'badge-prize-light', 'prize-badge-clickable']"
          role="button"
          @click.stop.prevent="addToGameHandler"
        >
          <template v-if="prizeCount && prizeVolume">
            <span class="badge-line prize-prefix">{{ prizeCount }} {{ prizeWord(prizeCount) }}</span>
            <span class="badge-line prize-term">v celkové hodnotě {{ prizeVolume }}</span>
          </template>
          <template v-else-if="prizeVolume">
            <span class="badge-line prize-prefix">Výhry</span>
            <span class="badge-line prize-term">v celkové hodnotě {{ prizeVolume }}</span>
          </template>
          <template v-else>
            <span class="badge-line prize-prefix">{{ prizeCount }} {{ prizeWord(prizeCount) }}</span>
          </template>
        </span>
      </div>

      <h4 class="game-title mb-2">{{ gameName }}</h4>

      <IngaPortrait
        class="reward-photo mb-3"
        frame="reward"
        :src="coverUrl"
        :alt="rewardName || 'Výhra'"
        :sources="galleryUrls"
        rotate
        :fade-ms="3200"
        :rotate-ms="4800"
        :rotate-delay="rotateDelay"
      />

      <div v-if="renderedGameText" class="game-text mb-3" v-html="renderedGameText"></div>

      <div class="duration-line mb-3">
        <span class="duration-heading">Trvání</span>
        <span class="duration-range">{{ fromLabel }} – {{ toLabel }}</span>
      </div>

      <div v-if="parseInt(gameTotalPlayer) > 0 || visiblePlayers.length" class="duration-frame mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2 gap-2">
          <div class="d-flex align-items-center min-w-0">
            <span class="duration-heading me-2">Obsazenost</span>
            <div v-if="visiblePlayers.length" class="player-faces">
              <button
                v-for="player in visiblePlayers"
                :key="player.UserID"
                type="button"
                class="player-face-btn"
                :title="player.UserNickName"
                @click.stop.prevent="goToUserPage(player)"
              >
                <img
                  :src="userAvatarUrl(player.UserImage, store.state.imageCacheKey, true)"
                  :alt="player.UserNickName"
                  class="player-face"
                >
              </button>
              <span v-if="hiddenPlayerCount" class="player-face-more">+{{ hiddenPlayerCount }}</span>
            </div>
          </div>
          <span v-if="parseInt(gameTotalPlayer) > 0" class="occupancy-count flex-shrink-0">{{ gameRegisteredPlayer }} / {{ gameTotalPlayer }}</span>
        </div>
        <div v-if="parseInt(gameTotalPlayer) > 0" class="progress duration-track">
          <div
            class="progress-bar duration-bar"
            role="progressbar"
            :style="{ width: occupancyPct + '%' }"
          ></div>
        </div>
      </div>

      <div class="row g-0 mx-0 finance-box mb-3">
        <div class="col-6 px-2 py-2 text-center border-end finance-col">
          <span class="finance-label">Obchodní kapitál</span>
          <span class="finance-value finance-capital">{{ formatNumber(gameCapital) }}</span>
        </div>
        <div class="col-6 px-2 py-2 text-center">
          <span class="finance-label">Vstupní vklad</span>
          <span class="finance-value">{{ formatNumber(gameCredit) }}</span>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-auto pt-2 gap-2">
        <button
          v-if="gameId"
          type="button"
          class="forum-open-btn"
          :title="'Fórum: ' + (gameName || 'hra')"
          @click.stop="openGameForum"
        >
          <fa-icon icon="fa-regular fa-comment-dots" />
          Fórum
        </button>
        <span v-else></span>
        <span
          v-if="isAlreadyRegistered"
          class="badge rounded-pill px-3 py-2 fw-bold text-uppercase badge-joined"
        >
          Jsi ve hře
        </span>
        <span
          v-else-if="registerOpen"
          class="badge rounded-pill px-3 py-2 fw-bold text-uppercase enter-btn"
          @click.stop="addToGameHandler"
        >
          Vstoupit do hry
        </span>
        <span v-else class="badge rounded-pill px-3 py-2 fw-bold text-uppercase badge-closed">
          Uzavřeno
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import IngaPortrait from "./IngaPortrait.vue";
import moment from "moment";
import { getLiveCountdownText, isRegisterActiveGlobal } from "@/utils/DateFunctions.js";
import { formatRichText } from "@/helpers/richText";
import { userAvatarUrl } from "@/helpers/userAvatars";

const props = defineProps({
  gameId: Number,
  gameRegister: String,
  gameFrom: String,
  gameTo: String,
  gameName: String,
  gameText: String,
  gameCredit: String,
  gameCapital: String,
  gameRewardImg: String,
  gameRewardImgs: {
    type: Array,
    default: () => []
  },
  gameRewardVolume: String,
  gameRewardCount: Number,
  gameRegisteredPlayer: Number,
  gameTotalPlayer: Number,
  gamePlayer: Array,
  rewardName: String,
  rotateSlot: {
    type: Number,
    default: 0
  }
});

const store = useStore();
const router = useRouter();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const maxFaces = 8;

const registeredPlayers = computed(() => Array.isArray(props.gamePlayer) ? props.gamePlayer : []);
const visiblePlayers = computed(() => registeredPlayers.value.slice(0, maxFaces));
const hiddenPlayerCount = computed(() => Math.max(0, registeredPlayers.value.length - maxFaces));
const currentUserId = computed(() => store.getters.getUser?.UserID);
const isAlreadyRegistered = computed(() => {
  const uid = currentUserId.value;
  if (!uid) return false;
  return registeredPlayers.value.some((player) => Number(player.UserID) === Number(uid));
});

const goToUserPage = (player) => {
  if (!player?.UserID) return;
  router.push({ name: "Detail her hráče", params: { uid: player.UserID } });
};
const now = ref(moment());
let timer = null;

const apiRoot = computed(() => process.env.VUE_APP_API_URL);

const rewardFileUrl = (file) => {
  const value = String(file || "").trim();
  if (!value) return "";
  if (value.startsWith("http")) return value;
  if (value.startsWith("img/")) return apiRoot.value + value;
  return apiRoot.value + "img/" + value;
};

const coverUrl = computed(() => rewardFileUrl(props.gameRewardImg));

const galleryUrls = computed(() => {
  const files = [];
  const add = (value) => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    const url = rewardFileUrl(value);
    if (url && !files.includes(url)) files.push(url);
  };
  add(props.gameRewardImg);
  add(props.gameRewardImgs);
  return files;
});

const rotateDelay = computed(() => Math.max(0, Number(props.rotateSlot) || 0) * 1000);

const parseFrom = () => moment(String(props.gameFrom || "").trim(), ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY"]);
const parseTo = () => moment(String(props.gameTo || "").trim(), ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY"]);

const fromLabel = computed(() => parseFrom().isValid() ? parseFrom().format("D. M. YYYY") : "");
const toLabel = computed(() => parseTo().isValid() ? parseTo().format("D. M. YYYY") : "");

const registerOpen = computed(() => isRegisterActiveGlobal(props.gameRegister, now.value));

const parseRegisterEnd = () => {
  const cleanStr = String(props.gameRegister || "").trim();
  const parts = cleanStr.split(" ");
  const formattedDate = (parts[0] || "").split(".").join("-");
  if (parts.length > 1) {
    return moment(`${formattedDate} ${parts[1]}`, "DD-MM-YYYY HH:mm:ss");
  }
  return moment(formattedDate, "DD-MM-YYYY").endOf("day");
};

const registerBadge = computed(() => {
  if (!registerOpen.value) return { prefix: "Registrace uzavřena", term: "" };
  const live = getLiveCountdownText(props.gameRegister, now.value);
  if (!live || live === "uzavřena") return { prefix: "Registrace uzavřena", term: "" };
  if (String(live).startsWith("za ") || live.startsWith("v ") || live.startsWith("ve ")) {
    return { prefix: "Registrace končí", term: live };
  }
  const end = parseRegisterEnd();
  if (end.isValid()) {
    return { prefix: "Registrace končí", term: end.format("D. M.") };
  }
  return { prefix: "Registrace končí", term: live };
});

const occupancyPct = computed(() => {
  const total = parseInt(props.gameTotalPlayer, 10);
  const registered = parseInt(props.gameRegisteredPlayer, 10);
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((registered / total) * 100)));
});

const renderedGameText = computed(() => formatRichText(props.gameText));

const prizeWord = (count) => {
  if (count === 1) return "výhra";
  if (count >= 2 && count <= 4) return "výhry";
  return "výher";
};

const prizeCount = computed(() => parseInt(props.gameRewardCount, 10) || 0);

const prizeVolume = computed(() => {
  const amount = Number(props.gameRewardVolume);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `${new Intl.NumberFormat("cs-CZ", {
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2
  }).format(amount)} Kč`;
});

const formatNumber = (number) => store.getters.formatNumber(number);

const addToGameHandler = () => {
  if (isAlreadyRegistered.value) {
    loadOpenGameDetail(props.gameId);
    return;
  }
  store.dispatch("addToGameHandler", {
    GameID: props.gameId,
    GameName: props.gameName,
    GameCredit: props.gameCredit
  });
};

const loadOpenGameDetail = (gameID) => {
  store.dispatch("loadOpenGameDetail", gameID);
};

const openGameForum = () => {
  if (!props.gameId) return;
  store.dispatch("openForum", {
    title: props.gameName || "Hra",
    GameID: props.gameId,
    Img: props.gameRewardImg
  });
};

const onCardClick = (event) => {
  if (event?.target?.closest?.(".register-badge, .prize-badge, .enter-btn, .forum-open-btn, .player-face-btn, .is-zoomable")) return;
  loadOpenGameDetail(props.gameId);
};

onMounted(() => {
  timer = setInterval(() => {
    now.value = moment();
  }, 30000);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.open-game-card {
  border-radius: 1rem !important;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;
}

.open-game-card:hover {
  transform: translateY(-3px);
}

.open-game-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.open-game-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0;
  box-shadow: none !important;
}

.register-badge,
.prize-badge {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.4rem;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.7rem;
  line-height: 1.1;
  white-space: normal;
  max-width: calc(50% - 0.25rem);
  padding: 0.2rem 0.75rem 0.14rem !important;
  border-radius: 0.55rem !important;
  box-sizing: border-box;
}

.register-badge {
  align-items: center;
  text-align: center;
  cursor: pointer;
}

.prize-badge {
  align-items: center;
  text-align: center;
}

.badge-line {
  display: block;
  line-height: 1.1;
}

.register-prefix,
.prize-prefix {
  position: relative;
  top: 3px;
}

.register-term,
.prize-term {
  color: #FF6B21;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 0;
  padding-bottom: 0;
}

.open-game-card-dark .register-term,
.open-game-card-dark .prize-term {
  color: #FF8A4C;
}

.prize-badge-clickable {
  cursor: pointer;
}

.badge-prize-light {
  background: #F1F5F9;
  color: #475569;
  font-size: 0.72rem;
}

.badge-prize-dark {
  background: #1E293B;
  color: #E2E8F0;
  border: 1px solid #334155;
  font-size: 0.72rem;
}

.game-title {
  font-weight: 900;
  font-size: 1.32rem;
  letter-spacing: -0.045em;
  line-height: 1.15;
  color: #FF6B21;
  -webkit-text-stroke: 0.25px currentColor;
}

.open-game-card-dark .game-title {
  color: #FF8A4C;
}

.game-text {
  font-size: 0.88rem;
  line-height: 1.5;
  color: #64748B;
  text-align: justify;
  hyphens: auto;
}

.game-text :deep(p) {
  margin-bottom: 0.5rem;
}

.game-text :deep(p:last-child) {
  margin-bottom: 0;
}

.game-text :deep(div),
.game-text :deep(p),
.game-text :deep(span),
.game-text :deep(li) {
  color: inherit;
}

.game-text :deep(strong),
.game-text :deep(b) {
  font-weight: 800;
  color: #FF6B21;
}

.game-text :deep(em),
.game-text :deep(i) {
  font-style: italic;
  color: inherit;
}

.open-game-card-dark .game-text {
  color: #CBD5E1;
}

.open-game-card-dark .game-text :deep(strong),
.open-game-card-dark .game-text :deep(b) {
  color: #FF8A4C;
}

.finance-box {
  border-radius: 0.85rem;
  overflow: hidden;
}

.open-game-card-light .finance-box {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.open-game-card-dark .finance-box {
  background: #1E293B;
  border: 1px solid #334155;
}

.open-game-card-dark .finance-col {
  border-color: #334155 !important;
}

.finance-label {
  display: block;
  text-transform: uppercase;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748B;
}

.open-game-card-dark .finance-label {
  color: #94A3B8;
}

.finance-value {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.05rem;
  font-weight: 900;
  color: #0F172A;
}

.open-game-card-dark .finance-value {
  color: #E2E8F0;
}

.finance-capital {
  color: #059669 !important;
}

.open-game-card-dark .finance-capital {
  color: #34D399 !important;
}

.duration-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.duration-range {
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  white-space: nowrap;
}

.open-game-card-dark .duration-range {
  color: #CBD5E1;
}

.duration-frame {
  padding: 10px 12px 8px;
  border-radius: 0.85rem;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
}

.open-game-card-dark .duration-frame {
  background: #1E293B;
  border-color: #64748B;
}

.duration-heading {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748B;
}

.open-game-card-dark .duration-heading {
  color: #94A3B8;
}

.duration-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
}

.open-game-card-dark .duration-label {
  color: #94A3B8;
}

.occupancy-count {
  font-size: 0.75rem;
  font-weight: 800;
  color: #0F172A;
}

.open-game-card-dark .occupancy-count {
  color: #E2E8F0;
}

.duration-track {
  height: 10px;
  border-radius: 999px;
  background: #E2E8F0;
  overflow: hidden;
}

.open-game-card-dark .duration-track {
  background: #0F172A;
}

.duration-bar {
  background: linear-gradient(90deg, #FF7A00 0%, #FF4D00 100%);
  border-radius: 999px;
}

.enter-btn {
  background: linear-gradient(135deg, #FF7A00 0%, #FF4D00 100%);
  color: #fff;
  font-size: 0.72rem;
  cursor: pointer;
}

.forum-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.forum-open-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.open-game-card-dark .forum-open-btn {
  border-color: #475569;
  color: #94A3B8;
}

.open-game-card-dark .forum-open-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.badge-closed {
  background: #64748B;
  color: #fff;
  font-size: 0.72rem;
}

.badge-joined {
  background: rgba(5, 150, 105, 0.14);
  color: #059669;
  font-size: 0.72rem;
  cursor: default;
}

.open-game-card-dark .badge-joined {
  background: rgba(52, 211, 153, 0.16);
  color: #34D399;
}

.reward-photo {
  width: 100%;
  max-width: 100%;
  display: block;
}

.open-game-card-light:hover {
  box-shadow: 0 15px 30px rgba(15, 23, 42, 0.1);
}

.open-game-card-dark:hover {
  border-color: rgba(255, 122, 0, 0.4) !important;
  box-shadow: 0 12px 24px rgba(255, 77, 0, 0.12) !important;
}

.player-faces {
  display: flex;
  align-items: center;
}

.player-face-btn {
  padding: 0;
  border: 0;
  background: transparent;
  line-height: 0;
  margin-left: -8px;
  cursor: pointer;
}

.player-face-btn:first-child {
  margin-left: 0;
}

.player-face {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffffff;
  display: block;
}

.open-game-card-dark .player-face {
  border-color: #0F172A;
}

.player-face-more {
  margin-left: 6px;
  font-size: 0.7rem;
  font-weight: 800;
  color: #64748B;
}

.open-game-card-dark .player-face-more {
  color: #94A3B8;
}
</style>
